import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import ProfileSetup from "./pages/ProfileSetup";
import Discovery from "./pages/Discovery";
import Matches from "./pages/Matches";
import Chat from "./pages/Chat";
import Pricing from "./pages/Pricing";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import Attestation from "./pages/Attestation";
import Register from "./pages/Register";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import { Toaster } from "./components/ui/sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const processAuth = async () => {
      // Public routes that don't require auth check
      const publicRoutes = ['/', '/register', '/login', '/pricing', '/forgot-password', '/reset-password', '/admin/login'];
      const isPublicRoute = publicRoutes.includes(location.pathname);
      const isAdminRoute = location.pathname.startsWith('/admin') && location.pathname !== '/admin/login';

      // Check for session_id in URL fragment (Google OAuth callback)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          const response = await axios.post(`${API}/auth/session`, {
            session_id: sessionId
          }, { withCredentials: true });
          
          // Clear URL fragment
          window.history.replaceState(null, '', window.location.pathname);
          
          // Check if user needs to attest
          if (!response.data.user.attested_healthcare_provider || !response.data.user.attested_shortage_area) {
            navigate('/attestation');
          } else {
            navigate('/dashboard');
          }
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/');
        }
      } else if (isAdminRoute) {
        // Handle admin routes separately
        try {
          await axios.get(`${API}/admin/me`, { withCredentials: true });
          // Admin is authenticated, allow access
          setLoading(false);
        } catch (error) {
          // Admin is not authenticated, redirect to admin login
          navigate('/admin/login');
          setLoading(false);
        }
      } else {
        // Check if already authenticated as regular user
        try {
          await axios.get(`${API}/auth/me`, { withCredentials: true });
          // User is authenticated
          if (location.pathname === '/' || location.pathname === '/register' || location.pathname === '/login') {
            // Redirect authenticated users away from auth pages
            navigate('/dashboard');
          } else {
            setLoading(false);
          }
        } catch (error) {
          // User is not authenticated
          if (!isPublicRoute) {
            // Redirect to login if trying to access protected route
            navigate('/login');
          }
          setLoading(false);
        }
      }
    };

    processAuth();
  }, [navigate, location]);

  // Show loading only for protected routes
  const publicRoutes = ['/', '/register', '/login', '/pricing', '/forgot-password', '/reset-password', '/admin/login'];
  if (loading && !publicRoutes.includes(location.pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/register" element={<Register />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/attestation" element={<Attestation />} />
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/profile-setup" element={<ProfileSetup />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/subscription-success" element={<SubscriptionSuccess />} />
      <Route path="/discovery" element={<Discovery />} />
      <Route path="/matches" element={<Matches />} />
      <Route path="/chat/:matchId" element={<Chat />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/dashboard" element={<AdminDashboard />} />
      <Route path="/admin/users" element={<AdminUsers />} />
      <Route path="/admin/subscriptions" element={<AdminSubscriptions />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthHandler />
      </BrowserRouter>
      <Toaster position="top-center" />
    </div>
  );
}

export default App;
